import { Text, Tooltip } from '@joor/design-system'
import { useSelector } from 'react-redux'

import {
  OrderPriceModifierType,
  OrderPriceRange,
} from '__generated__/atlas-types'

import { isAccountTypeBrand } from 'store/currentUser/selectors'

import { formatCurrencyPriceRangeStr } from '../../ProductsWidget/formatters'
import { FinancialSummaryTestIds } from '../FinancialSummaryWidget'
import {
  ConceptText,
  ConceptsContainer,
  NumericText,
  Row,
  StyledCard,
  StyledDivider,
  StyledIcon,
} from '../FinancialSummaryWidget.ui'
import CustomFees from './CustomFees'
import GatedFields from './GatedFields'
import TotalDue from './TotalDue'
import { OrderData } from 'features/Orders/Review/components/Overview/Overview'
import { FinancialSummaryIds } from 'features/Orders/Review/review.ids'

type FinancialsViewCardTypes = {
  canEdit?: boolean
  canViewDiscount?: boolean
  canViewJoorPayPriceModifiers?: boolean
  currencyCode: string
  retailCurrencyCode: string
  suggestedRetailTotal?: OrderPriceRange
  wholesaleTotal?: OrderPriceRange
  subTotal?: OrderPriceRange
  grandTotal?: OrderPriceRange
  dueTotal?: OrderPriceRange
  orderDiscount?: NonNullable<OrderData['financials']>['orderDiscount']
  tax?: NonNullable<OrderData['financials']>['tax']
  customFees?: NonNullable<OrderData['financials']>['customFees']
  shippingFees?: NonNullable<OrderData['financials']>['shippingFees']
  shippingRates?: NonNullable<
    OrderData['brand']['orderSettings']
  >['shippingRates']
  testId?: string
  onClickActionButton?: () => void
  paidAmount?: string
  remainingBalance?: string
  canViewGatedFields?: boolean
}

const FinancialsViewCard = ({
  canEdit = false,
  canViewDiscount = false,
  canViewJoorPayPriceModifiers = false,
  currencyCode,
  retailCurrencyCode,
  suggestedRetailTotal,
  wholesaleTotal,
  subTotal,
  grandTotal,
  dueTotal,
  orderDiscount,
  shippingFees,
  tax,
  customFees,
  shippingRates,
  testId,
  onClickActionButton,
  paidAmount,
  remainingBalance,
  canViewGatedFields = false,
}: FinancialsViewCardTypes) => {
  const isBrand = useSelector(isAccountTypeBrand)

  const taxAmountSet =
    (tax?.factor && tax.factor !== '0.00') ||
    (tax?.percentageAmount && tax.percentageAmount !== '0.00')

  const gatedFieldsAmounts = {
    taxes: tax?.factor ?? '0.00',
    customFees:
      customFees && customFees?.length > 0
        ? customFees
        : [
            {
              name: 'Custom fees',
              factor: '0.00',
              percentageAmount: '0',
              type: OrderPriceModifierType.NUMERIC,
            },
          ],
    totalDue: dueTotal ?? {
      maxPrice: '0',
      minPrice: '0',
    },
  }

  return (
    <StyledCard
      testId={testId}
      title="Financial Summary"
      {...(canEdit && {
        actionButtonTestId: FinancialSummaryTestIds.EditActionButton,
        actionButtonLabel: 'Edit',
        actionButtonId: FinancialSummaryIds.EditButtonId,
        onClickActionButton,
      })}
    >
      <ConceptsContainer>
        <Row>
          <div>
            <ConceptText>Total Order Value</ConceptText>
            <ConceptText
              data-testid={FinancialSummaryTestIds.SubtotalRetailNumber}
              className="light small"
            >
              {`(Total Retail Value ${formatCurrencyPriceRangeStr(
                retailCurrencyCode,
                suggestedRetailTotal,
              )})`}
            </ConceptText>
          </div>
          <NumericText>
            {formatCurrencyPriceRangeStr(currencyCode, wholesaleTotal)}
          </NumericText>
        </Row>
        <StyledDivider />
        <Row>
          <div>
            <ConceptText>Subtotal Wholesale</ConceptText>
            <ConceptText className="small">Incl. Product Discounts</ConceptText>
          </div>

          <NumericText
            data-testid={FinancialSummaryTestIds.SubtotalWholesaleNumber}
          >
            {formatCurrencyPriceRangeStr(currencyCode, subTotal)}
          </NumericText>
        </Row>
        {canViewDiscount && orderDiscount && (
          <Row>
            <ConceptText>Order Discount</ConceptText>
            <NumericText data-testid={FinancialSummaryTestIds.DiscountsNumber}>
              {`${orderDiscount.percentageAmount} 
          % (${currencyCode} -${orderDiscount.factor})`}
            </NumericText>
          </Row>
        )}
        {!canViewGatedFields &&
          (canViewJoorPayPriceModifiers || taxAmountSet) && (
            <Row>
              <ConceptText>Taxes</ConceptText>
              <NumericText data-testid={FinancialSummaryTestIds.TaxNumber}>
                {`${tax?.percentageAmount} 
          % (${currencyCode} +${tax?.factor})`}
              </NumericText>
            </Row>
          )}
        {shippingFees && (
          <Row>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <ConceptText>Shipping Fees</ConceptText>
              {isBrand && shippingRates && (
                <Tooltip
                  defaultPlacement="right"
                  content={
                    <div>
                      <span>Available Shipping Fees</span>
                      {isBrand && (
                        <Text fontSize="small" letterSpacing="normal">
                          These fees can be edited in your order settings.
                        </Text>
                      )}
                      <hr />
                      <div
                        style={{ whiteSpace: 'pre-line' }}
                        className="notranslate"
                      >
                        {shippingRates}
                      </div>
                    </div>
                  }
                  customTooltipStyles={{ 'margin-top': '-3px' }}
                >
                  <StyledIcon iconName="question" iconSize="small" />
                </Tooltip>
              )}
            </div>
            <NumericText data-testid={FinancialSummaryTestIds.FeesNumber}>
              {`${currencyCode} +${shippingFees?.factor}`}
            </NumericText>
          </Row>
        )}
        <Row className="total">
          <ConceptText>Grand Total</ConceptText>
          <NumericText data-testid={FinancialSummaryTestIds.TotalNumber}>
            {formatCurrencyPriceRangeStr(currencyCode, grandTotal)}
          </NumericText>
        </Row>
        {!canViewGatedFields &&
          (customFees && customFees?.length > 0 ? (
            <>
              <CustomFees customFees={customFees} currencyCode={currencyCode} />
              <TotalDue dueTotal={dueTotal} currencyCode={currencyCode} />
            </>
          ) : canViewJoorPayPriceModifiers && !customFees?.length ? (
            <>
              <Row style={{ marginTop: '5px' }}>
                <ConceptText>Custom fees</ConceptText>
                <NumericText
                  id={FinancialSummaryTestIds.CustomFeeAmount}
                >{`${currencyCode} +0.00`}</NumericText>
              </Row>
              <TotalDue dueTotal={dueTotal} currencyCode={currencyCode} />
            </>
          ) : null)}
        {paidAmount && (
          <Row className="total" style={{ marginTop: '5px' }}>
            <ConceptText>Paid</ConceptText>
            <NumericText data-testid={FinancialSummaryTestIds.TotalNumber}>
              {`${currencyCode} ${paidAmount}`}
            </NumericText>
          </Row>
        )}
        {remainingBalance && (
          <Row className="total" style={{ marginTop: '5px' }}>
            <ConceptText>Remaining</ConceptText>
            <NumericText data-testid={FinancialSummaryTestIds.TotalNumber}>
              {`${currencyCode} ${remainingBalance}`}
            </NumericText>
          </Row>
        )}
        {canViewGatedFields && (
          <GatedFields
            currencyCode={currencyCode}
            amounts={gatedFieldsAmounts}
          />
        )}
        {wholesaleTotal?.minPrice !== wholesaleTotal?.maxPrice && (
          <ConceptText className="light small padding-top">
            Totals are based on the highest sku price per style when bulk
            quantities are used. Assign sized quantities to see final totals.
          </ConceptText>
        )}
        {canViewDiscount && (
          <ConceptText className="light small padding-top">
            Discount and fee values are rounded and may not add up to total.
            Refer to totals for final calculated values.
          </ConceptText>
        )}
      </ConceptsContainer>
    </StyledCard>
  )
}

export default FinancialsViewCard
